import { format, parseISO } from 'date-fns';
import {
  Dispatch,
  FunctionComponent,
  memo,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import sprite from '../../../../assets/icons/sprite.svg';
import { ChannelContext, ConfigContext, UserContext } from '../../../../context';
import {
  ChannelRole,
  OrganisationRole,
  OrganisationUserOrderBy,
  User,
  UserRole,
} from '../../../../services';
import {
  Avatar,
  BadgeList,
  Check,
  getAriaSort,
  getChannelDomain,
  getCssVar,
  getWebAppUrl,
  IconLabel,
  OrderDirection,
  ORG_DASH_USERS,
  PaginationInfo,
  useIsNarrowViewport,
  useRestoreFocusAfterFetch,
  useUsersTable,
} from '../../../../shared';
import { UserChannels } from '../UserChannelsModal';
import { GetOrgUsersArgs } from '../Users';
import { UsersTableColumn } from '../UsersColumns';

import classNames from 'classnames';
import classes from './UsersTable.module.scss';

interface SortHeaderArgs {
  orderBy: OrganisationUserOrderBy;
  label: string;
}

interface UsersTableProps {
  isFetched: boolean;
  users: User[];
  selectedUsers: User[];
  setSelectedUsers: Dispatch<SetStateAction<User[]>>;
  orderByActive: OrganisationUserOrderBy;
  setOrderByActive: Dispatch<SetStateAction<OrganisationUserOrderBy>>;
  orderDirectionActive: OrderDirection;
  setOrderDirectionActive: Dispatch<SetStateAction<OrderDirection>>;
  getUsers: (args: GetOrgUsersArgs) => void;
  pageInfo: PaginationInfo;
  bulkActionsComponent: JSX.Element;
  setUserChannels?: Dispatch<SetStateAction<UserChannels | null>>;
}

export const UsersTable: FunctionComponent<UsersTableProps> = memo(
  ({
    isFetched,
    users,
    selectedUsers,
    setSelectedUsers,
    orderByActive,
    setOrderByActive,
    orderDirectionActive,
    setOrderDirectionActive,
    getUsers,
    pageInfo,
    bulkActionsComponent,
    setUserChannels,
  }) => {
    const { t } = useTranslation();

    const { t: ctxT } = useTranslation('', { keyPrefix: 'orgDashboardUsers' });

    const { getTableColumnLabel } = useUsersTable();

    const isNarrowViewport = useIsNarrowViewport();

    const restoreFocusAfterFetch = useRestoreFocusAfterFetch(isFetched);

    const { id: userId } = useContext(UserContext).userProfile;

    const {
      role: { id: roleId },
    } = useContext(ChannelContext).channel;

    const { excludeColumns } = useContext(ConfigContext).config.elements.orgDashboard.users;

    const [cookies] = useCookies([ORG_DASH_USERS]);

    const tableRef = useRef<HTMLTableElement>(null);

    const previousPageRef = useRef<HTMLButtonElement>(null);

    const nextPageRef = useRef<HTMLButtonElement>(null);

    const orgDashUsersCookies = useMemo(() => {
      return { ...cookies[ORG_DASH_USERS] };
    }, [cookies]);

    const activeColumnsDefault = useMemo(
      () => [
        UsersTableColumn.NAME,
        UsersTableColumn.EMAIL,
        UsersTableColumn.LAST_ACTIVE,
        UsersTableColumn.INVITED,
      ],
      []
    );

    const { activeColumns = activeColumnsDefault } = orgDashUsersCookies;

    const selectedUserIds = useMemo(() => selectedUsers.map(({ id }) => id), [selectedUsers]);

    const allowToSelect = useMemo(
      () =>
        [
          ChannelRole.ADMIN,
          OrganisationRole.ADMIN,
          OrganisationRole.OWNER,
          OrganisationRole.TEAM_MEMBER,
        ].includes(roleId),
      [roleId]
    );

    const sortBy = useCallback(
      (orderBy: OrganisationUserOrderBy) => {
        const orderDirection =
          orderBy !== orderByActive
            ? OrderDirection.ASC
            : orderDirectionActive === OrderDirection.DESC
            ? OrderDirection.ASC
            : OrderDirection.DESC;

        restoreFocusAfterFetch(() =>
          tableRef.current?.querySelector<HTMLElement>('th[aria-sort] button')
        );

        setOrderByActive(orderBy);
        setOrderDirectionActive(orderDirection);
      },
      [
        orderByActive,
        orderDirectionActive,
        restoreFocusAfterFetch,
        setOrderByActive,
        setOrderDirectionActive,
      ]
    );

    const sortHeader = useCallback(
      ({ orderBy, label }: SortHeaderArgs) => {
        const isSorted = orderBy === orderByActive;

        return (
          <th {...(isSorted && { 'aria-sort': getAriaSort(orderDirectionActive) })}>
            <button
              type={'button'}
              className={classes['users__table-sort-header']}
              onClick={() => sortBy(orderBy)}
              onFocus={({ currentTarget }) =>
                currentTarget.scrollIntoView({ block: 'nearest', inline: 'nearest' })
              }
            >
              {label}
              {isSorted && (
                <svg className={classes['users__table-sort']}>
                  <use
                    href={`${sprite}#${
                      orderDirectionActive === OrderDirection.DESC ? 'down' : 'up'
                    }-arrow`}
                  />
                </svg>
              )}
            </button>
          </th>
        );
      },
      [orderByActive, orderDirectionActive, sortBy]
    );

    const onSelectChange = useCallback(
      (user: User) => {
        if (selectedUserIds.includes(user.id)) {
          setSelectedUsers((selectedUsersPrev) =>
            selectedUsersPrev.filter(({ id }) => id !== user.id)
          );
          return;
        }

        setSelectedUsers((selectedUsersPrev) => [...selectedUsersPrev, user]);
      },
      [selectedUserIds, setSelectedUsers]
    );

    const onNameClick = useCallback(
      (id: number) => {
        const url = `${getWebAppUrl(getChannelDomain())}/admin${
          id === userId ? '/profile' : `/profile/${id}`
        }`;

        window.open(url, '_blank', 'noopener, noreferrer');
      },
      [userId]
    );

    const bulkActions = useMemo(() => {
      if (!selectedUsers.length) {
        return null;
      }

      return <div className={classes['users__bulk-actions']}>{bulkActionsComponent}</div>;
    }, [bulkActionsComponent, selectedUsers.length]);

    const pageBackHandler = useCallback(() => {
      restoreFocusAfterFetch(() =>
        previousPageRef.current?.disabled ? nextPageRef.current : previousPageRef.current
      );

      getUsers({ page: pageInfo.page - 1 });
    }, [getUsers, pageInfo.page, restoreFocusAfterFetch]);

    const pageForwardHandler = useCallback(() => {
      restoreFocusAfterFetch(() =>
        nextPageRef.current?.disabled ? previousPageRef.current : nextPageRef.current
      );

      getUsers({ page: pageInfo.page + 1 });
    }, [getUsers, pageInfo.page, restoreFocusAfterFetch]);

    const pagination = useMemo(() => {
      const { page, perPage, hasNextPage, hasPreviousPage, totalItems } = pageInfo;

      if (!totalItems) {
        return null;
      }

      const from = (page - 1) * perPage + 1;

      const to = Math.min(page * perPage, totalItems);

      return (
        <div className={classes['users__pagination']}>
          <IconLabel
            ref={previousPageRef}
            iconSize={20}
            iconId={'arrow-left'}
            ariaLabel={t('common.previous-page')}
            disabled={!hasPreviousPage}
            onClick={pageBackHandler}
            color={getCssVar('--base-link-text-color')}
            hoverColor={getCssVar('--base-link-text-hover-color')}
          />

          <span className={classes['users__pagination-label']}>
            {from}-{to} {t('common.of')} {totalItems}
          </span>

          <IconLabel
            ref={nextPageRef}
            iconSize={20}
            iconId={'arrow-right'}
            ariaLabel={t('common.next-page')}
            disabled={!hasNextPage}
            onClick={pageForwardHandler}
            color={getCssVar('--base-link-text-color')}
            hoverColor={getCssVar('--base-link-text-hover-color')}
          />
        </div>
      );
    }, [pageBackHandler, pageForwardHandler, pageInfo, t]);

    if (!isFetched) {
      return (
        <div className={classes['users__loader']}>
          <Skeleton height={'3rem'} />
          <Skeleton height={'3rem'} />
          <Skeleton height={'3rem'} />
        </div>
      );
    }

    const activeColumnsFiltered = activeColumns.filter(
      (column: UsersTableColumn) => !excludeColumns.includes(column)
    );

    return (
      <>
        <div
          className={classNames(classes['users__table-wrapper'], {
            [classes['users__table-wrapper--narrow']]: isNarrowViewport,
          })}
        >
          <table ref={tableRef} className={classes['users__table']}>
            <thead>
              <tr>
                {/* <th>
                  <Check checked={isSelectAllChecked} onChange={onSelectAllChange} />
                </th> */}

                <th style={{ minWidth: '1rem' }}></th>

                {sortHeader({ orderBy: OrganisationUserOrderBy.SCREEN_NAME, label: ctxT('NAME') })}

                {sortHeader({ orderBy: OrganisationUserOrderBy.EMAIL, label: ctxT('EMAIL') })}

                {activeColumnsFiltered.includes(UsersTableColumn.ID) && <th>{ctxT('ID')}</th>}

                {activeColumnsFiltered.includes(UsersTableColumn.GENDER) && (
                  <th>{getTableColumnLabel(UsersTableColumn.GENDER)}</th>
                )}

                {activeColumnsFiltered.includes(UsersTableColumn.POSITION) && (
                  <th>{getTableColumnLabel(UsersTableColumn.POSITION)}</th>
                )}

                {activeColumnsFiltered.includes(UsersTableColumn.DEPARTMENT) && (
                  <th>{getTableColumnLabel(UsersTableColumn.DEPARTMENT)}</th>
                )}

                {activeColumnsFiltered.includes(UsersTableColumn.SUBSCRIPTION) && (
                  <th>{getTableColumnLabel(UsersTableColumn.SUBSCRIPTION)}</th>
                )}

                {activeColumnsFiltered.includes(UsersTableColumn.LOCATION) && (
                  <th>{getTableColumnLabel(UsersTableColumn.LOCATION)}</th>
                )}

                {activeColumnsFiltered.includes(UsersTableColumn.USER_TAGS) && (
                  <th>{ctxT('USER_TAGS')}</th>
                )}

                {activeColumnsFiltered.includes(UsersTableColumn.CHANNELS_COUNT) && (
                  <th>{ctxT('CHANNELS_COUNT')}</th>
                )}

                {activeColumnsFiltered.includes(UsersTableColumn.REFERRAL_COUNT) && (
                  <th>{ctxT('REFERRAL_COUNT')}</th>
                )}

                {activeColumnsFiltered.includes(UsersTableColumn.REFERRED_BY_USER) && (
                  <th>{ctxT('REFERRED_BY_USER')}</th>
                )}

                {sortHeader({
                  orderBy: OrganisationUserOrderBy.AUTHORIZED_TIME,
                  label: ctxT('LAST_ACTIVE'),
                })}

                {sortHeader({
                  orderBy: OrganisationUserOrderBy.REGISTERED,
                  label: ctxT('INVITED'),
                })}

                {/* {activeColumnsFiltered.includes(TableColumn.COMMENTS_COUNT) && (
                  <th>{ctxT('COMMENTS_COUNT')}</th>
                )} */}

                {/* {activeColumnsFiltered.includes(TableColumn.REACTIONS_COUNT) && (
                  <th>{ctxT('REACTIONS_COUNT')}</th>
                )} */}

                {/* {activeColumnsFiltered.includes(TableColumn.LIKES_ON_COMMENTS_COUNT) && (
                  <th>{ctxT('LIKES_ON_COMMENTS_COUNT')}</th>
                )} */}
                {/* 
                {activeColumnsFiltered.includes(TableColumn.SHARES_COUNT) && (
                  <th>{ctxT('SHARES_COUNT')}</th>
                )} */}
                {/* 
                {activeColumnsFiltered.includes(TableColumn.POSTS_COUNT) && (
                  <th>{ctxT('POSTS_COUNT')}</th>
                )} */}
              </tr>
            </thead>
            <tbody>
              {users.map((user) => {
                const {
                  id: userId,
                  avatar,
                  screenName,
                  email,
                  companyId,
                  gender,
                  position,
                  department,
                  subscription,
                  location,
                  accessedChannels,
                  authorizedAt,
                  registeredAt,
                  organisationTags,
                  referralUsersCount,
                  referredByUser,
                  // statistic: { comments, likesOnComments, posts, reactions, shares },
                  isBanned,
                  roleId,
                } = user;

                // const isChecked = selectAll !== userIds.includes(userId);

                const isOrgOwnerOrAdmin = [
                  UserRole.ORGANISATION_OWNER,
                  UserRole.ORGANISATION_ADMIN,
                ].includes(roleId as UserRole);

                const isChecked = selectedUserIds.includes(userId);

                return (
                  <tr key={userId} className={classNames({ [classes['selected-row']]: isChecked })}>
                    <td>
                      <Check
                        checked={isChecked}
                        ariaLabel={screenName}
                        onChange={() => onSelectChange(user)}
                        disabled={isOrgOwnerOrAdmin || !allowToSelect}
                      />
                    </td>

                    <td>
                      <div className={classes['users__table-name-wrapper']}>
                        <Avatar url={avatar?.url} nonClickable />

                        <button
                          type={'button'}
                          className={classes['users__table-name']}
                          onClick={() => onNameClick(userId)}
                        >
                          {screenName}
                        </button>

                        {isBanned && (
                          <IconLabel
                            iconId={'ban'}
                            iconSize={18}
                            data={ctxT('user-banned')}
                            color={getCssVar('--color-danger')}
                            hoverColor={getCssVar('--color-danger-hover')}
                            nonClickable
                          />
                        )}

                        {setUserChannels && (
                          <IconLabel
                            iconId={'channels'}
                            iconSize={18}
                            ariaLabel={ctxT('CHANNELS_COUNT')}
                            className={classes['users__table-name-channels']}
                            onClick={() =>
                              setUserChannels({
                                userId,
                                screenName,
                                accessedChannels,
                                isOrgOwnerOrAdmin,
                                showNotifyUsers: Boolean(email?.trim().length && !isBanned),
                              })
                            }
                          />
                        )}
                      </div>
                    </td>

                    <td>
                      <div className={classes['users__table-email']}>{email}</div>
                    </td>

                    {activeColumns.includes(UsersTableColumn.ID) && <td>{companyId}</td>}

                    {activeColumns.includes(UsersTableColumn.GENDER) && (
                      <td>{gender ? t(`genderTypes.${gender}`) : ''}</td>
                    )}

                    {activeColumns.includes(UsersTableColumn.POSITION) && <td>{position}</td>}

                    {activeColumns.includes(UsersTableColumn.DEPARTMENT) && <td>{department}</td>}

                    {activeColumns.includes(UsersTableColumn.SUBSCRIPTION) && (
                      <td>{subscription}</td>
                    )}

                    {activeColumns.includes(UsersTableColumn.LOCATION) && <td>{location?.text}</td>}

                    {activeColumns.includes(UsersTableColumn.USER_TAGS) && (
                      <td>
                        <BadgeList items={organisationTags} />
                      </td>
                    )}

                    {activeColumns.includes(UsersTableColumn.CHANNELS_COUNT) && (
                      <td>{accessedChannels.length}</td>
                    )}

                    {activeColumns.includes(UsersTableColumn.REFERRAL_COUNT) && (
                      <td>{referralUsersCount}</td>
                    )}

                    {activeColumns.includes(UsersTableColumn.REFERRED_BY_USER) && (
                      <td>{referredByUser?.screenName}</td>
                    )}

                    <td>
                      {authorizedAt
                        ? format(parseISO(authorizedAt), 'MMM dd, yyyy')
                        : t('common.inactive')}
                    </td>

                    <td>{format(parseISO(registeredAt), 'MMM dd, yyyy')}</td>

                    {/* {activeColumns.includes(TableColumn.COMMENTS_COUNT) && <td>{comments}</td>} */}

                    {/* {activeColumns.includes(TableColumn.REACTIONS_COUNT) && <td>{reactions}</td>} */}

                    {/* {activeColumns.includes(TableColumn.LIKES_ON_COMMENTS_COUNT) && (
                      <td>{likesOnComments}</td>
                    )} */}

                    {/* {activeColumns.includes(TableColumn.SHARES_COUNT) && <td>{shares}</td>} */}

                    {/* {activeColumns.includes(TableColumn.POSTS_COUNT) && <td>{posts}</td>} */}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className={classes['users__bottom-pane']}>
          {!Boolean(users.length) && (
            <div className={classes['users__bottom-pane-empty']}>{ctxT('no-users-found')}</div>
          )}
          {bulkActions}
          {pagination}
        </div>
      </>
    );
  }
);
